import gql from 'graphql-tag';

import { clients } from '../../../util/clients';
import { fieldsToGqlString, mergeByIdNewAtTop } from '../../../util/shared/vue-global';

const { backend } = clients.direct;

const bidHistoryFields = {
  _id: '',
  bidDate: '',
  regNumber: '',
  bidderName: '',
  bidAmount: '',
};

const commissionersSaleFields = {
  _id: '',
  systemId: '',
  propertyOrParcelId: '',
  taxId: '',
  saleYear: '',
  taxYear: '',
  ownerName: '',
  situsAddress: '',
  addition: '',
  assessedValue: '',
  legalDesc: '',
  isItSold: '',
  commissionerPubDateOne: '',
  commissionerPubDateTwo: '',
  commissionerPubDateThree: '',
  commissionerDateOfSale: '',
  commissionerTimeOfSale: '',
  commissionerDateOfMeeting: '',
  commissionerSaleFees: '',
  bidHistory: {
    ...bidHistoryFields,
  },
};

export const commissionersSaleAction = {
  async loadCommissionersSale({ commit, dispatch }, { criteria, baseCrudKey, postProcessTrans }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query commissionersSale($criteria: commissionersSaleCriteriaType) {
            commissionersSale(criteria: $criteria) {
              ${fieldsToGqlString(commissionersSaleFields)},
            }
          }
        `,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      if (baseCrudKey && results.data) {
        const processedData = postProcessTrans
          ? postProcessTrans(results.data.commissionersSale)
          || [] : results.data.commissionersSale;
        commit('base/crud/setItems', [baseCrudKey, processedData], { root: true });
        return processedData;
      }
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting tax commissioner resale.', { root: true });
    }
    return [];
  },
  /**
   * The accounts that "Print County Deed" is about to put on ONE deed for this record. Comes back
   * with more than one taxId only when the county has Commissioner Deeds set to Print Combined and
   * this bidder won several properties at this sale. The backend answers from the same grouping the
   * deed report uses, so the confirmation can never disagree with the PDF.
   */
  async loadCommissionersSaleDeedGroup({ commit, dispatch }, { id }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.query({
        query: gql`
          query commissionersSaleDeedGroup($id: ID!) {
            commissionersSaleDeedGroup(id: $id) {
              taxIds,
              bidderName,
              combinedBidAmount,
            }
          }
        `,
        variables: { id },
        fetchPolicy: 'no-cache',
      });
      clients.handleGQLErrors(results, commit);
      return (results.data && results.data.commissionersSaleDeedGroup) || null;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem checking which accounts the deed will cover.', { root: true });
    }
    return null;
  },
  async pullAllRecordsForCurrentCommissionersSale({ commit, dispatch },
    { itemToSent }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation pullAllRecordsForCurrentCommissionersSale($itemToSent: createcommissionersSaleInputType!)  {
          pullAllRecordsForCurrentCommissionersSale(upsert: $itemToSent) {
                statusCode,
              }
            }`,
        variables: {
          itemToSent,
        },
      });
      const { pullAllRecordsForCurrentCommissionersSale } = results.data;
      if (pullAllRecordsForCurrentCommissionersSale.statusCode === 200) dispatch('flashSuccess', 'Commisioner records pulled Successfully', { root: true });
      // a 201 used to be silent, so a refused or part-written pull looked like nothing happened
      else if (pullAllRecordsForCurrentCommissionersSale.statusCode === 201) dispatch('flashInfo', 'No commissioner records were pulled - check the Sale Year in Resale Settings', { root: true });
      return pullAllRecordsForCurrentCommissionersSale;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem updating commissioner records', { root: true });
    }
    return {};
  },
  async getCommissionersSaleCountBasedOnSaleYear({ commit, dispatch },
    { itemToSent }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation getCommissionersSaleCountBasedOnSaleYear($itemToSent: createcommissionersSaleInputType!)  {
          getCommissionersSaleCountBasedOnSaleYear(upsert: $itemToSent) {
                totalRecords,
              }
            }`,
        variables: {
          itemToSent,
        },
      });
      if (results.data && results.data.getCommissionersSaleCountBasedOnSaleYear) {
        return results.data.getCommissionersSaleCountBasedOnSaleYear.totalRecords;
      }
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem getting commissioner count based on sale year', { root: true });
    }
    return {};
  },
  async addANewRecordToCommissionersSale({ commit, dispatch },
    { itemToSent }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation addANewRecordToCommissionersSale($itemToSent: createcommissionersSaleInputType!)  {
          addANewRecordToCommissionersSale(upsert: $itemToSent) {
                statusCode,
              }
            }`,
        variables: {
          itemToSent,
        },
      });
      const { addANewRecordToCommissionersSale } = results.data;
      if (addANewRecordToCommissionersSale.statusCode === 200) dispatch('flashSuccess', 'A new Commisioner record added Successfully', { root: true });
      else if (addANewRecordToCommissionersSale.statusCode === 202) dispatch('flashInfo', 'Record not found with the combination or the status code not assigned to that tax record', { root: true });
      else dispatch('flashWarn', 'There was a problem adding Commisioner records', { root: true });
      return addANewRecordToCommissionersSale;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem adding Commisioner records', { root: true });
    }
    return {};
  },
  async upsertCommissionersSaleSelectedFields({ commit, dispatch },
    { itemToSent }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation upsertCommissionersSaleSelectedFields($itemToSent: commissionersSaleInputType!)  {
          upsertCommissionersSaleSelectedFields(upsert: $itemToSent) {
                statusCode,
              }
            }`,
        variables: {
          itemToSent,
        },
      });
      const { upsertCommissionersSaleSelectedFields } = results.data;
      if (upsertCommissionersSaleSelectedFields.statusCode === 200) dispatch('flashSuccess', 'Commisioner record updated Successfully', { root: true });
      if (upsertCommissionersSaleSelectedFields.statusCode === 201) dispatch('flashInfo', 'There was a problem upserting commissioner records', { root: true });
      return upsertCommissionersSaleSelectedFields;
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem upserting commissioner records', { root: true });
    }
    return {};
  },
  async upsertCommissionersSale({ dispatch, rootGetters, commit },
    { item, baseCrudKey, postProcessTrans }) {
    let results;

    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation upsertCommissionersSale($item:commissionersSaleInputType) {
          upsertCommissionersSale(upsert:$item) {
            ${(fieldsToGqlString(commissionersSaleFields))}
          }
        }`,
        variables: {
          item,
        },
      });

      clients.handleGQLErrors(results, commit);
      const taxData = rootGetters['base/crud/items'](baseCrudKey);
      const newRecord = results.data.upsertCommissionersSale;
      const newList = mergeByIdNewAtTop(taxData || [], newRecord);
      const processedTransData = postProcessTrans
        ? postProcessTrans(newList) : newList;
      commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
    } catch (error) {
      dispatch('logException', error, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem in upserting the commissioner upsert', { root: true });
    }
  },
  async deleteCommissionersSale({ commit, rootGetters, dispatch }, { item, baseCrudKey }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      results = await backend.mutate({
        mutation: gql`mutation deleteCommissionersSale($item: createcommissionersSaleInputType!)  {
          deleteCommissionersSale(upsert: $item) {
                statusCode,
              }
            }`,
        variables: {
          item,
        },
      });
      clients.handleGQLErrors(results, commit);
      const { deleteCommissionersSale } = results.data;
      if (deleteCommissionersSale.statusCode === 200) {
        const delId = item._id;
        const taxData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = taxData.filter((d) => d._id !== delId);
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
        commit('base/crud/setIsVisible', [baseCrudKey, false], { root: true });
        dispatch('flashSuccess', 'Commisioner Resale record deleted Successfully', { root: true });
      } else if (deleteCommissionersSale.statusCode === 201) dispatch('flashInfo', 'There was a problem deleting the commissioner record', { root: true });
    } catch (e) {
      dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem deleting the commissioner record', { root: true });
    }
  },
};

export default commissionersSaleAction;
